import { gql } from '@apollo/client/core';

import { SnapshotsFieldFragment } from '@/graphql/hasura/cameras';
import {
  IntrusionPanelDeviceFieldsFragment,
  IntrusionPartitionStateFieldsFragment,
  IntrusionZoneAssignmentSummaryFragment
} from '@/graphql/hasura/intrusion-fragments';

const ZoneEditableFieldsFragment = gql`
  fragment ZoneEditableFieldsFragment on IntrusionZone {
    id
    description
    placeID
    Place {
      id
      name
      hierarchy {
        name
      }
    }
  }
`;

/**
 * `IntrusionZone` carries its own `deviceID`/`Device` (the panel it is wired to, same shape as
 * `IntrusionPartition.Device`) and its own `Place`, so a zone can be queried as a first-class
 * root entity without traversing through `PartitionZoneAssignments`. That join table is only
 * needed to resolve which partition(s) a zone is assigned to (see `ZoneDetailFragment`).
 */
export const ZoneSearchFragment = gql`
  fragment ZoneSearchFragment on IntrusionZone {
    ...ZoneEditableFieldsFragment
    id
    name
    index
    type
    eIntrusionZoneType {
      comment
      value
    }
    state
    stateValues
    alarm
    alarmValues
    canBypass
    defaultVideoSourceID
    deviceID
    # Linked camera + its latest snapshot for the zones list/card thumbnail.
    VideoSource {
      id
      ...SnapshotsFieldFragment
    }
    Device {
      ...IntrusionPanelDeviceFieldsFragment
      deviceAddress
    }
    PartitionZoneAssignments(where: { enabled: { _eq: true } }) {
      enabled
      partitionID
      IntrusionPartition {
        id
        name
        index
        stateValues
        alarmValues
      }
    }
  }
  ${SnapshotsFieldFragment}
  ${ZoneEditableFieldsFragment}
  ${IntrusionPanelDeviceFieldsFragment}
`;

export const GQL_ZONE_SEARCH = gql`
  query GQL_ZONE_SEARCH(
    $where: IntrusionZone_bool_exp
    $limit: Int
    $offset: Int
    $orderBy: [IntrusionZone_order_by!]
  ) {
    IntrusionZone(limit: $limit, offset: $offset, where: $where, order_by: $orderBy) {
      ...ZoneSearchFragment
    }
  }
  ${ZoneSearchFragment}
`;

export const GQL_SUB_ZONE_SEARCH = gql`
  subscription GQL_SUB_ZONE_SEARCH(
    $where: IntrusionZone_bool_exp
    $limit: Int
    $offset: Int
    $orderBy: [IntrusionZone_order_by!]
  ) {
    IntrusionZone(limit: $limit, offset: $offset, where: $where, order_by: $orderBy) {
      ...ZoneSearchFragment
    }
  }
  ${ZoneSearchFragment}
`;

export const ZoneDetailFragment = gql`
  fragment ZoneDetailFragment on IntrusionZone {
    ...ZoneEditableFieldsFragment
    id
    name
    index
    type
    state
    stateValues
    alarm
    alarmValues
    canBypass
    defaultVideoSourceID
    deviceID
    VideoSource {
      id
      name
      place {
        id
        name
      }
      # Camera device status + audio/analytics stats, consumed by CameraSmartIcon in the zone status strip.
      enabled
      deviceConnectionStatus
      gatewayConnectionStatus
      profileMSupported
      metadataEnabled
      device {
        id
        enabled
      }
      audioSources {
        audioSource {
          id
          enabled
        }
      }
    }
    Device {
      ...IntrusionPanelDeviceFieldsFragment
      description
    }
    PartitionZoneAssignments(where: { enabled: { _eq: true } }) {
      enabled
      partitionID
      IntrusionPartition {
        ...IntrusionPartitionStateFieldsFragment
        PartitionZoneAssignments(where: { enabled: { _eq: true } }) {
          ...IntrusionZoneAssignmentSummaryFragment
        }
      }
    }
  }
  ${ZoneEditableFieldsFragment}
  ${IntrusionPanelDeviceFieldsFragment}
  ${IntrusionPartitionStateFieldsFragment}
  ${IntrusionZoneAssignmentSummaryFragment}
`;

export const GQL_GET_ZONE_BY_ID = gql`
  query GQL_GET_ZONE_BY_ID($id: String!) {
    IntrusionZone_by_pk(id: $id) {
      ...ZoneDetailFragment
    }
  }
  ${ZoneDetailFragment}
`;

export const GQL_SUB_ZONE_BY_ID = gql`
  subscription GQL_SUB_ZONE_BY_ID($id: String!) {
    IntrusionZone_by_pk(id: $id) {
      ...ZoneDetailFragment
    }
  }
  ${ZoneDetailFragment}
`;

export const GQL_UPDATE_ZONE_BY_ID = gql`
  mutation GQL_UPDATE_ZONE_BY_ID($id: String!, $zone: IntrusionZone_set_input!) {
    update_IntrusionZone_by_pk(pk_columns: { id: $id }, _set: $zone) {
      ...ZoneEditableFieldsFragment
      defaultVideoSourceID
      VideoSource {
        id
        name
        place {
          id
          name
        }
      }
    }
  }
  ${ZoneEditableFieldsFragment}
`;
